import { getVariant, Box, Close, InputProps } from '@apia/theme';
import {
  getDateFormat,
  getLabel,
  isChild,
  TDateFormat,
  TModify,
  useUpdateEffect,
} from '@apia/util';
import dayjs from 'dayjs';
import uniqueId from 'lodash-es/uniqueId';
import React from 'react';
import { icons } from '@apia/icons';
import globalFocus from '../../globalFocus';
import { useModal } from '../modals';
import { CalendarModal } from '../modals/CalendarModal';
import { FieldErrorMessage } from './FieldErrorMessage';
import { IconInput, IIconInput } from './IconInput';
import { getFieldErrorStyles } from './util/style';

declare global {
  interface Window {
    LANG_CODE: string;
    MSG_INVALID_DATE: string;
    MSG_FEC_FIN_MAY_FEC_INI: string;
    LBL_PICK_DATE: string;
  }
}

const DEFAULT_LOCALE = window.LANG_CODE;
const DEFAULT_MASK_PLACEHOLDER = '_';

const dateFormat: TDateFormat = getDateFormat();

const getMaskForDateFormat = (): string => {
  if (dateFormat === 'YYYY/MM/DD') return '9999/99/99';
  return '99/99/9999';
};

export type TDateProps = TModify<
  InputProps,
  {
    allowPickBeforeToday?: boolean;
    error?: string | null;
    /**
     * Con esta propiedad se puede proveer una función que será utilizada para
     * determinar qué máscara debe utilizar el input
     */
    getCustomMask?: () => string;
    isLoading?: boolean;
    /**
     * Recibe la nueva fecha. En caso de devolver
     * false desde este método, el nuevo valor no
     * se persistirá.
     */
    onChange?: (newValue: string) => boolean | string | void;
    /**
     * Es llamado cada vez que alguien presiona el botón de eliminar fecha.
     */
    onDelete?: () => void;
    onError?: (error: string | null) => unknown;
    /**
     * Con esta propiedad se puede proveer una función que recibirá como
     * parámetro el valor actual del cmapo y debe devolver el valor a mostrar.
     */
    renderLabel?: (value: string) => string;
    value?: string;
  }
> &
  Pick<IIconInput, 'buttonProps'>;

export const DateInput = React.forwardRef<HTMLInputElement, TDateProps>(
  (
    {
      allowPickBeforeToday,
      buttonProps: outerButtonProps,
      className,
      error,
      getCustomMask,
      isLoading,
      onBlur,
      onChange,
      onDelete,
      onError,
      renderLabel,
      value: outerValue,
      ...props
    },
    ref,
  ) => {
    const id = React.useMemo(uniqueId, []);
    const [inputValue, setInputValue] = React.useState(outerValue ?? '');
    const [calValue, setCalValue] = React.useState<Date>();
    const boxRef = React.useRef<HTMLElement | null>(null);
    const { show, hide, ...modalProps } = useModal({
      preFetcher() {
        globalFocus.focus = () =>
          boxRef.current?.querySelector('.iconButton') as HTMLElement;
        return new Promise((resolve) => {
          let currentSelectedDate = dayjs(inputValue, dateFormat);
          if (!currentSelectedDate.isValid())
            currentSelectedDate = dayjs(new Date());
          setCalValue(currentSelectedDate.toDate());
          resolve();
        });
      },
    });
    const [stateError, setError] = React.useState<null | string>(error ?? null);

    const lastEmittedValue = React.useRef(outerValue ?? '');
    useUpdateEffect(() => {
      setInputValue(outerValue ?? '');
      lastEmittedValue.current = outerValue as string;
    }, [outerValue]);

    useUpdateEffect(() => {
      setError(error ?? null);
    }, [error]);

    useUpdateEffect(() => {
      if (onError) onError(stateError);
    }, [stateError]);

    const shoutOnChange = React.useCallback(
      (newValue: string) => {
        if (newValue !== lastEmittedValue.current && onChange) {
          const result = onChange(newValue);
          if (result) {
            lastEmittedValue.current = newValue;
          }
          return result;
        }
        return true;
      },
      [onChange],
    );

    const setCalendarValue = React.useCallback(
      (value: string, nocheck?: boolean) => {
        if (value === '') {
          void shoutOnChange('');
          setInputValue('');
          return;
        }
        let newDate: dayjs.Dayjs;
        if (value !== '') {
          // format cal needs: Tue Feb 09 2021 00:00:00 GMT-0300
          newDate = dayjs(value, dateFormat);
          if (!newDate.isValid()) newDate = dayjs(new Date());
        } else {
          newDate = dayjs(new Date());
        }
        const formattedDate = newDate.format(dateFormat);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (formattedDate !== inputValue && nocheck !== true) {
          void shoutOnChange('');
          setInputValue('');
          setError(window.MSG_INVALID_DATE);
        } else if (allowPickBeforeToday === false && newDate.isBefore(today)) {
          void shoutOnChange('');
          setInputValue('');
          setError(window.MSG_FEC_FIN_MAY_FEC_INI);
        } else {
          const shoutResult = shoutOnChange(value === '' ? '' : formattedDate);
          if (shoutResult === false) {
            setInputValue(shoutResult || '');
            setError(null);
          } else if (typeof shoutResult === 'string') {
            setInputValue('');
            setError(shoutResult);
          } else {
            setInputValue(formattedDate);
            setError(null);
          }
        }
      },
      [allowPickBeforeToday, inputValue, shoutOnChange],
    );

    const selectDate = React.useCallback(
      (val: Date): void => {
        hide();
        const newValue = dayjs(val).format(dateFormat);
        setCalendarValue(newValue, true);
      },
      [hide, setCalendarValue],
    );

    const onCloseCalendarModal = React.useCallback(() => {
      hide();
    }, [hide]);

    const handleBlur = React.useCallback(
      (ev: React.FocusEvent<HTMLInputElement>) => {
        if (
          onBlur &&
          !isChild(ev.relatedTarget as HTMLElement, (current) => {
            return (
              current.id === `DateModal${id}` || current.id === `DateInput${id}`
            );
          })
        ) {
          onBlur(ev);
        }
      },
      [id, onBlur],
    );

    const inputProps = React.useMemo(
      () => ({
        ...props,
        className: 'dateInput',
        value: renderLabel ? renderLabel(inputValue) : inputValue,
        ref,
        onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
          setInputValue(ev.target.value);
          if (
            getMaskForDateFormat().replaceAll('9', DEFAULT_MASK_PLACEHOLDER) !==
              ev.target.value &&
            ev.target.value !== ''
          )
            setError(null);
        },
        onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
          if (!(props.readOnly || getCustomMask))
            setCalendarValue(ev.target.value);
          handleBlur(ev);
        },
        onKeyDown: (ev: React.KeyboardEvent) => {
          if (ev.key === 'Enter') setCalendarValue(inputValue);
        },
      }),
      [
        getCustomMask,
        handleBlur,
        inputValue,
        props,
        ref,
        renderLabel,
        setCalendarValue,
      ],
    );

    const buttonProps = React.useMemo(
      () => ({
        disabled: props.disabled ?? props.readOnly,
        'aria-label': window.LBL_PICK_DATE,
        ...outerButtonProps,
      }),
      [outerButtonProps, props.disabled, props.readOnly],
    );

    const additionalButtons = React.useMemo(
      () => (
        <Close
          className="delete_date_button"
          onClick={() => {
            setCalendarValue('');
            onDelete?.();
            (
              document.querySelector(
                `#DateInput${id} input`,
              ) as HTMLInputElement
            )?.focus();
          }}
          type="button"
          title={getLabel('btnAiDelete').text}
          aria-label={getLabel('btnAiDelete').text}
        />
      ),
      [id, onDelete, setCalendarValue],
    );

    return (
      <>
        <CalendarModal
          {...modalProps}
          calValue={calValue}
          locale={DEFAULT_LOCALE}
          handleClickDay={selectDate}
          onCancel={onCloseCalendarModal}
          id={`DateModal${id}`}
        />
        <Box
          {...getVariant('forms.dateInput')}
          className={`${className ?? ''} dateInput`}
          id={`DateInput${id}`}
          ref={boxRef}
        >
          <IconInput
            additionalButtons={
              inputValue &&
              inputValue !==
                getMaskForDateFormat().replaceAll(
                  '9',
                  DEFAULT_MASK_PLACEHOLDER,
                ) &&
              !props.readOnly &&
              !props.disabled
                ? additionalButtons
                : undefined
            }
            isLoading={isLoading}
            maskPlaceholder={DEFAULT_MASK_PLACEHOLDER}
            mask={getCustomMask ? getCustomMask() : getMaskForDateFormat()}
            icon={icons.Calendar}
            onBlur={handleBlur}
            buttonProps={buttonProps}
            inputProps={inputProps}
            readOnly={props.readOnly}
            sx={getFieldErrorStyles(stateError === null)}
            onClick={show}
          />
          {stateError && <FieldErrorMessage>{stateError}</FieldErrorMessage>}
        </Box>
      </>
    );
  },
);

DateInput.displayName = 'DateInput';
